import { StyledTableLink } from '@common/Dependency/styled';
import { EntityIcon } from '@common/Entity/EntityIcons';
import {
  useAvailableTeamProjects,
  useRemoveDefaultProject,
  useAddDefaultProject,
  useAvailableTeamDefaultProjects
} from '@common/projectHooks';
import { TeamAvailableProjects } from '@common/queries/team';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { TrashCIcon } from '@prophecy/icons';
import { ExtractArrayType, NestedData } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Stack, Table, Text, StackItem, ColumnsType, Badges, Select, TableActions, SortOrder } from '@prophecy/ui-v3';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { generateAvailableProjectOptions } from '../../../CreateEntity/Project/common';

const StyledSelect = styled(Select)`
  width: 300px;
`;

export function DefaultProjects({ teamId }: { teamId: string }) {
  const [idToRemove, setIdToRemove] = useState<string>();
  const reset = () => setIdToRemove(undefined);
  const { removeDefaultProject, isLoading: isRemoveProjectDependencyLoading } = useRemoveDefaultProject(reset, reset);
  const { addDefaultProject, isLoading: isAddProjectDependencyLoading } = useAddDefaultProject();

  const [value, setValue] = useState<string>();
  //HACK: to keep select box empty after selection
  useEffect(() => {
    if (value) {
      setValue(undefined);
    }
  }, [value]);

  const {
    availableProjectDependencies: availableTeamPythonProjects,
    isInitialLoading: isAvailableTeamPythonProjectsLoading
  } = useAvailableTeamProjects(false, BackEndCodeLanguage.python, teamId);
  const {
    availableProjectDependencies: availableTeamScalaProjects,
    isInitialLoading: isAvailableTeamScalaProjectsLoading
  } = useAvailableTeamProjects(false, BackEndCodeLanguage.scala, teamId);
  const {
    availableProjectDependencies: availableTeamSQlProjects,
    isInitialLoading: isAvailableTeamSQlProjectsLoading
  } = useAvailableTeamProjects(false, BackEndCodeLanguage.sql, teamId);
  const availableTeamProjects = (availableTeamPythonProjects || [])
    .concat(availableTeamScalaProjects || [])
    .concat(availableTeamSQlProjects || []);
  const isAvailableTeamProjectsLoading =
    isAvailableTeamPythonProjectsLoading || isAvailableTeamScalaProjectsLoading || isAvailableTeamSQlProjectsLoading;
  const { availableDefaultDependencies: availableTeamPythonDefaultProjects } = useAvailableTeamDefaultProjects(
    BackEndCodeLanguage.python,
    teamId
  );
  const { availableDefaultDependencies: availableTeamScalaDefaultProjects } = useAvailableTeamDefaultProjects(
    BackEndCodeLanguage.scala,
    teamId
  );
  const { availableDefaultDependencies: availableTeamSQlDefaultProjects } = useAvailableTeamDefaultProjects(
    BackEndCodeLanguage.sql,
    teamId
  );
  const _selectedProjects = (availableTeamPythonDefaultProjects || [])
    .concat(availableTeamScalaDefaultProjects || [])
    .concat(availableTeamSQlDefaultProjects || []);
  const selectedProjectIds = _selectedProjects.map((project) => project._id);
  const selectedProjects = availableTeamProjects.filter((item) => selectedProjectIds.includes(item._id));

  async function onSelect(value: string) {
    setValue(value);
    await addDefaultProject(teamId, [Number(value)]);
  }
  async function onRemoveDependency(id: string) {
    setIdToRemove(id);
    await removeDefaultProject(teamId, [Number(id)]);
  }
  const isLoading = isAddProjectDependencyLoading || isRemoveProjectDependencyLoading || isAvailableTeamProjectsLoading;
  const Columns: ColumnsType<
    ExtractArrayType<NonNullable<TeamAvailableProjects['Team']>['availableProjectDependencies']>
  > = [
    {
      title: 'Project',
      dataIndex: 'name',
      key: 'name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search project',
          'data-test-id': 'default-projects-search-input'
        }
      },
      render: (value: string, record: NestedData) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
            <span>
              <EntityIcon size='xs' entity={Entity.Project} />
            </span>
            <StyledTableLink
              to={Private_Routes.Project.home.getUrl({ uid: record._id })}
              data-test-id='default-projects-project-link'>
              {renderColumnWithEllipsis(value)}
            </StyledTableLink>
          </Stack>
        );
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: renderColumnWithEllipsis
    },

    {
      title: 'Version',
      dataIndex: 'latestReleaseVersion',
      key: 'latestReleaseVersion',
      sortable: true,
      render: renderColumnWithEllipsis
    },

    {
      dataIndex: 'actions',
      key: 'actions',
      width: '60px',
      align: 'center',
      render: (v: string, record: NestedData) => {
        const id = record._id;
        const disabled = idToRemove === id;
        return (
          <TableActions align='end'>
            <Table.IconButton
              onClick={() => onRemoveDependency(id)}
              loading={disabled}
              disabled={disabled}
              icon={<TrashCIcon type='default' size='xs' />}
              data-test-id={`default-projects-remove-${id}-button`}
              aria-label='Remove default project'
            />
          </TableActions>
        );
      }
    }
  ];
  return (
    <Stack gap={theme.spaces.x20} height='100%'>
      <Stack direction='horizontal' align='space-between'>
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <Text level='md'>Default Projects</Text>
          <Badges tone='gray'>{selectedProjects?.length}</Badges>
        </Stack>
        <StyledSelect
          optionFilterProp={'title'}
          value={value}
          onChange={onSelect}
          filterOption={true}
          notFoundContent='No Project found'
          showSearch
          backfill
          loading={isAvailableTeamProjectsLoading || isAddProjectDependencyLoading}
          placeholder='Add projects'
          data-test-id='default-projects-add-projects-select'
          aria-label='Add projects'>
          {generateAvailableProjectOptions(availableTeamProjects, selectedProjectIds)}
        </StyledSelect>
      </Stack>
      <StackItem grow='1'>
        <Table
          columns={Columns}
          virtualize={true}
          height='100%'
          dataSource={selectedProjects?.map((item) => ({ rowKey: item._id, ...item }))}
          loading={isLoading}
          sortConfig={{ sortOrder: SortOrder.desc, sortColumn: 'created' }}
          data-test-id='default-projects-table'
        />
      </StackItem>
    </Stack>
  );
}
